/**
 * session/quests — the thin glue between the pure quest engine (core/quests) and the
 * live GameSession (owner decision D17). Pure helpers only; the stateful methods live
 * on GameSession. The renderer never owns rules — objectives auto-tick off the real
 * play events the session already emits, and the whole layer is ignorable + pressure-
 * free (quests never fail, never expire, never gate the cozy loop).
 *
 * Spec: notes/story-design/quest-system.md (§4 wiring, §5 rewards).
 */
import type {
  Beat,
  ChatChannel,
  PokemonInstance,
  QuestGiverId,
  QuestReward,
  QuestState,
  SpeciesData,
} from '@/shared';
import type { QuestWorldSlice } from '@/core/quests.js';

/** Minimal species lookup the quest engine folds catch/cast events against. */
export interface SpeciesTypeSource {
  speciesById(id: string): SpeciesData | undefined;
}

/**
 * A structured quest moment the voice layer phrases (Agent E's beat factory renders
 * these into `quest-progress` / `quest-complete` Beats). Kept here so GameSession can
 * emit quest moments defensively even before the chat-director's quest BeatKinds land.
 */
export interface QuestBeatInfo {
  readonly kind: 'quest-progress' | 'quest-complete';
  readonly questId: string;
  readonly title: string;
  readonly giver: QuestGiverId;
  readonly channel: ChatChannel;
  /** The stage that just cleared (absent for a whole-quest completion). */
  readonly stageIndex?: number;
}

/**
 * Turns a quest moment into voice Beats. Wired by Agent E once the quest BeatKinds
 * exist in the chat-director; until then GameSession simply no-ops (see
 * {@link GameSession.setQuestBeatFactory} / `recordQuestEvent`). Decoupled from the
 * frozen `BeatKind` enum on purpose so this layer compiles independently of Agent E.
 */
export type QuestBeatFactory = (info: QuestBeatInfo) => readonly Beat[];

/** Optional targeting for a reward grant (which mon a candy/stone lands on). */
export interface GrantRewardOpts {
  /** Instance id of the mon a rare-candy / evolution-stone reward targets (default: lead). */
  readonly targetInstanceId?: string;
  /** Nickname for a gift-mon (overrides the reward's own nickname). */
  readonly nickname?: string;
}

/** The outcome of granting a quest reward — enough for a warm toast + tests. */
export interface GrantRewardResult {
  readonly kind: QuestReward['kind'];
  /** Where a gift-mon landed. */
  readonly placement?: 'team' | 'box';
  /** The gifted / candied / evolved mon. */
  readonly instance?: PokemonInstance;
  readonly badgeId?: string;
  readonly entryId?: string;
  readonly propId?: string;
  readonly cosmeticId?: string;
  /** False ⇒ a warm no-op (e.g. no eligible mon for a stone); never an error. */
  readonly ok: boolean;
}

/** The read-only species-type lookup {@link advanceQuests} needs (catch/cast events). */
export function speciesTypeLookup(index: SpeciesTypeSource): {
  speciesType(id: string): string | undefined;
} {
  return { speciesType: (id) => index.speciesById(id)?.types[0] };
}

/**
 * Assemble the soft-visibility world facts {@link offerableQuests} consults. Pure —
 * these gate whether a quest is *shown*, never whether the game progresses.
 */
export function buildQuestWorldSlice(args: {
  quests: QuestState;
  caughtCount: number;
  friendshipWith(trainerId: string): number;
}): QuestWorldSlice {
  return {
    badges: args.quests.badges,
    completedQuestIds: args.quests.completed,
    caughtCount: args.caughtCount,
    friendshipWith: args.friendshipWith,
  };
}
